// Approval state display for a proposed solution
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { CheckCircle, XCircle, Clock, AlertCircle, ShieldCheck, Loader2 } from 'lucide-react'
import { useApproval } from '@/hooks/useApproval'
import { useAuth } from '@/hooks/useAuth'
import { useState } from 'react'

interface ApprovalStatusProps {
  issueId: string
}

const STATUS_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  pending: { icon: <Clock className="h-4 w-4" />, color: 'bg-kaycoo-blue-pale text-kaycoo-blue-dark border-kaycoo-blue', label: 'Pending' },
  under_review: { icon: <AlertCircle className="h-4 w-4" />, color: 'bg-amber-50 text-amber-800 border-amber-300', label: 'Under Review' },
  approved: { icon: <CheckCircle className="h-4 w-4" />, color: 'bg-kaycoo-teal-pale text-kaycoo-teal-dark border-kaycoo-teal', label: 'Approved' },
  rejected: { icon: <XCircle className="h-4 w-4" />, color: 'bg-kaycoo-burgundy-pale text-kaycoo-burgundy-dark border-kaycoo-burgundy', label: 'Rejected' },
  changes_requested: { icon: <AlertCircle className="h-4 w-4" />, color: 'bg-amber-50 text-amber-800 border-amber-300', label: 'Changes Requested' },
}

export function ApprovalStatus({ issueId }: ApprovalStatusProps): JSX.Element {
  const { user } = useAuth()
  const { validations, isLoading, approve, reject, requestChanges } = useApproval(issueId)
  const [rejectReason, setRejectReason] = useState('')
  const [changeRequest, setChangeRequest] = useState('')
  const [showRejectForm, setShowRejectForm] = useState(false)
  const [showChangeForm, setShowChangeForm] = useState(false)
  const [isActing, setIsActing] = useState(false)

  if (isLoading || validations.length === 0) return <></>

  const latestValidation = validations[0] as Record<string, unknown>
  const status = String(latestValidation.status ?? 'pending')
  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.pending

  const isValidator = String(latestValidation.validator_id ?? '') === user?.id

  const handleApprove = async () => {
    setIsActing(true)
    try {
      await approve(String(latestValidation.id))
    } finally {
      setIsActing(false)
    }
  }

  const handleReject = async () => {
    if (!rejectReason.trim()) return
    setIsActing(true)
    try {
      await reject({ validationId: String(latestValidation.id), reason: rejectReason })
      setShowRejectForm(false)
      setRejectReason('')
    } finally {
      setIsActing(false)
    }
  }

  const handleRequestChanges = async () => {
    if (!changeRequest.trim()) return
    setIsActing(true)
    try {
      await requestChanges({ validationId: String(latestValidation.id), changes: changeRequest })
      setShowChangeForm(false)
      setChangeRequest('')
    } finally {
      setIsActing(false)
    }
  }

  return (
    <Card className="border-kaycoo-teal-light bg-kaycoo-white">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm text-kaycoo-teal-dark">
          <ShieldCheck className="h-4 w-4" />
          Approval Status
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Badge variant="outline" className={`${config.color} flex w-fit items-center gap-1.5`}>
          {config.icon}
          {config.label}
        </Badge>

        {status === 'rejected' && Boolean(latestValidation.rejection_reason) && (
          <div className="rounded-md bg-kaycoo-burgundy-pale p-2 text-sm text-kaycoo-burgundy-dark">
            <p className="font-medium">Rejection reason:</p>
            <p>{String(latestValidation.rejection_reason)}</p>
          </div>
        )}

        {status === 'changes_requested' && Boolean(latestValidation.change_requests) && (
          <div className="rounded-md bg-amber-50 p-2 text-sm text-amber-800">
            <p className="font-medium">Changes requested:</p>
            <p>{String(latestValidation.change_requests)}</p>
          </div>
        )}

        {status === 'approved' && (
          <div className="rounded-md bg-kaycoo-teal-pale p-2 text-sm text-kaycoo-teal-dark">
            Solution approved. Ready to apply.
          </div>
        )}

        {/* Validator actions */}
        {isValidator && (status === 'pending' || status === 'under_review') && (
          <div className="space-y-2 border-t border-kaycoo-white-3 pt-3">
            {!showRejectForm && !showChangeForm && (
              <div className="flex gap-2">
                <Button
                  onClick={handleApprove}
                  disabled={isActing}
                  size="sm"
                  className="flex-1 bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
                >
                  {isActing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-1.5 h-4 w-4" />}
                  Approve
                </Button>
                <Button onClick={() => setShowRejectForm(true)} variant="outline" size="sm" className="flex-1">
                  <XCircle className="mr-1.5 h-4 w-4" />
                  Reject
                </Button>
                <Button onClick={() => setShowChangeForm(true)} variant="outline" size="sm">
                  <AlertCircle className="h-4 w-4" />
                </Button>
              </div>
            )}

            {showRejectForm && (
              <div className="space-y-2">
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Reason for rejection..."
                  rows={2}
                />
                <div className="flex gap-2">
                  <Button onClick={() => setShowRejectForm(false)} variant="outline" size="sm">Cancel</Button>
                  <Button onClick={handleReject} disabled={!rejectReason.trim() || isActing} size="sm" variant="destructive">
                    Confirm Reject
                  </Button>
                </div>
              </div>
            )}

            {showChangeForm && (
              <div className="space-y-2">
                <Textarea
                  value={changeRequest}
                  onChange={(e) => setChangeRequest(e.target.value)}
                  placeholder="Describe changes needed..."
                  rows={2}
                />
                <div className="flex gap-2">
                  <Button onClick={() => setShowChangeForm(false)} variant="outline" size="sm">Cancel</Button>
                  <Button onClick={handleRequestChanges} disabled={!changeRequest.trim() || isActing} size="sm">
                    Request Changes
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
